import axios, {
  AxiosError,
  AxiosHeaders,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import type { ApiErrorBody, ClientApiError } from "./types";

const CSRF_HEADER = "X-CSRF-Token";
const LOCAL_API_BASE_URL = "/api/v1";

type CsrfConfig = InternalAxiosRequestConfig & {
  _csrfRetried?: boolean;
  _skipCsrf?: boolean;
};

let csrfToken: string | null = null;
let csrfInFlight: Promise<string> | null = null;
let unauthenticatedHandler: (() => void) | null = null;

export function resetCsrfToken(): void {
  csrfToken = null;
  csrfInFlight = null;
}

export function getCachedCsrfToken(): string | null {
  return csrfToken;
}

export function setUnauthenticatedHandler(handler: (() => void) | null): void {
  unauthenticatedHandler = handler;
}

function isMutatingMethod(method?: string): boolean {
  const m = (method ?? "get").toLowerCase();
  return m === "post" || m === "put" || m === "patch" || m === "delete";
}

function readApiError(error: AxiosError<ApiErrorBody>): ClientApiError {
  if (!error.response) {
    return {
      status: null,
      code: "NETWORK_ERROR",
      message: "לא ניתן להתחבר לשרת. בדקו את החיבור ונסו שוב.",
      isNetworkError: true,
    };
  }

  const body = error.response.data;
  const code = body?.error?.code ?? "UNKNOWN_ERROR";
  const correlationId = body?.error?.correlationId;

  if (error.response.status === 401) {
    return {
      status: 401,
      code,
      message:
        code === "INVALID_CREDENTIALS"
          ? "אימייל או סיסמה שגויים"
          : "יש להתחבר מחדש",
      correlationId,
      isNetworkError: false,
    };
  }

  if (error.response.status === 403 && code === "CSRF_REJECTED") {
    return {
      status: 403,
      code,
      message: "בקשה נדחתה מסיבות אבטחה. נסו שוב.",
      correlationId,
      isNetworkError: false,
    };
  }

  if (error.response.status === 403 && code === "TENANT_MEMBERSHIP_MISSING") {
    return {
      status: 403,
      code,
      message: "אין הרשאה לבחור ארגון זה",
      correlationId,
      isNetworkError: false,
    };
  }

  if (error.response.status === 429) {
    return {
      status: 429,
      code,
      message: "יותר מדי ניסיונות. נסו שוב בעוד רגע.",
      correlationId,
      isNetworkError: false,
    };
  }

  if (error.response.status === 500 || code === "INTERNAL_ERROR") {
    return {
      status: error.response.status,
      code: code === "UNKNOWN_ERROR" ? "INTERNAL_ERROR" : code,
      message: "שגיאה בלתי צפויה. נסו שוב.",
      correlationId,
      isNetworkError: false,
    };
  }

  return {
    status: error.response.status,
    code,
    message:
      (typeof body?.error?.message === "string" && body.error.message.trim()
        ? body.error.message
        : null) || "אירעה שגיאה. נסו שוב.",
    correlationId,
    isNetworkError: false,
  };
}

export function toClientApiError(error: unknown): ClientApiError {
  if (
    error &&
    typeof error === "object" &&
    "code" in error &&
    "message" in error &&
    "isNetworkError" in error
  ) {
    return error as ClientApiError;
  }
  if (axios.isAxiosError(error)) {
    return readApiError(error as AxiosError<ApiErrorBody>);
  }
  return {
    status: null,
    code: "UNKNOWN_ERROR",
    message: "אירעה שגיאה. נסו שוב.",
    isNetworkError: false,
  };
}

export function resolveApiBaseUrl(
  configuredUrl = process.env.REACT_APP_API_BASE_URL,
): string {
  const trimmed = configuredUrl?.trim();
  if (!trimmed) {
    return LOCAL_API_BASE_URL;
  }
  // A scheme-less host (e.g. "api.example.com") would be treated by axios as a
  // relative path on the client's own origin — assume https instead.
  const withScheme =
    /^https?:\/\//i.test(trimmed) || trimmed.startsWith("/")
      ? trimmed
      : `https://${trimmed}`;
  const withoutTrailingSlash = withScheme.replace(/\/+$/, "");
  if (withoutTrailingSlash.toLowerCase().endsWith(LOCAL_API_BASE_URL)) {
    return withoutTrailingSlash;
  }
  return `${withoutTrailingSlash}${LOCAL_API_BASE_URL}`;
}

export function createHttpClient(baseURL = resolveApiBaseUrl()): AxiosInstance {
  const client = axios.create({
    baseURL,
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  async function ensureCsrf(): Promise<string> {
    if (csrfToken) {
      return csrfToken;
    }
    if (!csrfInFlight) {
      csrfInFlight = client
        .get<{ csrfToken: string }>("/auth/csrf", {
          _skipCsrf: true,
        } as CsrfConfig)
        .then((res) => {
          csrfToken = res.data.csrfToken;
          return csrfToken;
        })
        .finally(() => {
          csrfInFlight = null;
        });
    }
    return csrfInFlight;
  }

  client.interceptors.request.use(async (config) => {
    const cfg = config as CsrfConfig;
    if (!isMutatingMethod(cfg.method)) {
      return cfg;
    }

    // Axios omits the default Content-Type when `data` is undefined. Our CSRF guard
    // requires application/json (or multipart) and returns 415 otherwise — so empty
    // POSTs like chat accept/reject must still send JSON Content-Type + a body.
    const headers = AxiosHeaders.from(cfg.headers ?? {});
    const currentType = String(headers.get("Content-Type") ?? "").toLowerCase();
    const isMultipart =
      currentType.includes("multipart/form-data") ||
      (typeof FormData !== "undefined" && cfg.data instanceof FormData);
    if (!isMultipart) {
      if (!currentType.includes("application/json")) {
        headers.set("Content-Type", "application/json");
      }
      if (cfg.data === undefined) {
        cfg.data = {};
      }
    }

    if (cfg._skipCsrf) {
      cfg.headers = headers;
      return cfg;
    }
    const token = await ensureCsrf();
    headers.set(CSRF_HEADER, token);
    cfg.headers = headers;
    return cfg;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiErrorBody>) => {
      const cfg = error.config as CsrfConfig | undefined;
      const status = error.response?.status;
      const code = error.response?.data?.error?.code;

      if (
        status === 403 &&
        code === "CSRF_REJECTED" &&
        cfg &&
        !cfg._csrfRetried &&
        isMutatingMethod(cfg.method)
      ) {
        resetCsrfToken();
        cfg._csrfRetried = true;
        const token = await ensureCsrf();
        const headers = AxiosHeaders.from(cfg.headers ?? {});
        headers.set(CSRF_HEADER, token);
        cfg.headers = headers;
        return client.request(cfg);
      }

      if (status === 401 && unauthenticatedHandler) {
        const url = cfg?.url ?? "";
        if (!url.includes("/auth/login")) {
          unauthenticatedHandler();
        }
      }

      return Promise.reject(error);
    },
  );

  return client;
}

export const httpClient = createHttpClient();
